import { GB_PATH, NI_PATH, VIEW_W, VIEW_H, project, resolvePoint, SEV_FILL } from "@/lib/ukmap";

const SEV_RANK = { severe: 3, warning: 2, advisory: 1, normal: 0 };
const CELL = 6; // grid size (in viewBox units) for clustering nearby pins

// Homepage overview map: plots current alerts at their real UK location and
// clusters tightly-packed pins into a single counted marker for readability.
export function UkAlertMap({ items = [], className = "" }) {
  const pts = [];
  items.forEach((it) => {
    let lat = it.lat, lon = it.lon;
    if (lat == null || lon == null) {
      const p = resolvePoint(it.area, it.nation);
      if (p) { lat = p[0]; lon = p[1]; }
    }
    if (lat == null || lon == null) return;
    const [x, y] = project(lat, lon);
    pts.push({ x, y, sev: it.severity });
  });

  // Cluster by grid cell
  const buckets = {};
  pts.forEach((p) => {
    const k = `${Math.round(p.x / CELL)}_${Math.round(p.y / CELL)}`;
    (buckets[k] = buckets[k] || []).push(p);
  });
  const clusters = Object.values(buckets).map((grp) => {
    const x = grp.reduce((s, p) => s + p.x, 0) / grp.length;
    const y = grp.reduce((s, p) => s + p.y, 0) / grp.length;
    const sev = grp.reduce((top, p) => (SEV_RANK[p.sev] > SEV_RANK[top] ? p.sev : top), "normal");
    return { x, y, sev, count: grp.length };
  });

  return (
    <div className={className} data-testid="uk-alert-map">
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} className="w-full h-auto" role="img" aria-label="Map of the UK showing current alert locations">
        <path d={GB_PATH} fill="#e2e8f0" stroke="#cbd5e1" strokeWidth="0.6" />
        <path d={NI_PATH} fill="#e2e8f0" stroke="#cbd5e1" strokeWidth="0.6" />
        {clusters.map((c, i) => {
          const fill = SEV_FILL[c.sev] || "#64748b";
          if (c.count > 1) {
            const r = Math.min(3.2 + c.count * 0.45, 7);
            return (
              <g key={`c-${c.x}-${c.y}`} data-testid="map-cluster">
                <circle cx={c.x} cy={c.y} r={r + 2} fill={fill} opacity="0.18" />
                <circle cx={c.x} cy={c.y} r={r} fill={fill} stroke="#fff" strokeWidth="0.8" />
                <text x={c.x} y={c.y} textAnchor="middle" dominantBaseline="central" fill="#fff"
                  fontSize={r > 5 ? 4 : 3.4} fontWeight="700" fontFamily="ui-sans-serif, system-ui">{c.count}</text>
              </g>
            );
          }
          return (
            <g key={`p-${c.x}-${c.y}-${i}`} data-testid="map-pin">
              <circle cx={c.x} cy={c.y} r={c.sev === "severe" ? 4.5 : 3.2} fill={fill} opacity="0.2">
                {c.sev === "severe" && <animate attributeName="r" values="3;6;3" dur="2s" repeatCount="indefinite" />}
                {c.sev === "severe" && <animate attributeName="opacity" values="0.28;0.05;0.28" dur="2s" repeatCount="indefinite" />}
              </circle>
              <circle cx={c.x} cy={c.y} r="1.7" fill={fill} stroke="#fff" strokeWidth="0.6" />
            </g>
          );
        })}
      </svg>
    </div>
  );
}
